"use client";

import { Box, Center, Flex, HStack, Text } from "@chakra-ui/react";
import { NextLink } from "@/_components/ui/NextLink";
import { useTranslations } from "next-intl";
import { PublicHeader } from "@/_components/layout/PublicHeader";
import { SubNavDropdown, SubNavMobileAccordion } from "./redesign/SubNav";

interface HeaderProps {
  headerColor: string;
  headerBgColor: string;
}

export const Header = ({ headerColor }: HeaderProps) => {
  const t = useTranslations("landing.header");

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const rect = element.getBoundingClientRect();
      const scrollTop =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0;
      const offsetPosition = rect.top + scrollTop - headerHeight;
      requestAnimationFrame(() => {
        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: "smooth",
        });
      });
    }
  };

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    sectionId: string,
  ) => {
    e.preventDefault();
    scrollToSection(sectionId);
  };

  const navigationItems = [
    { sectionId: "knoot-agent", href: "#knoot-agent", label: t("knoot-agent") },
    // { sectionId: "commitments", href: "#commitments", label: t("commitments") },
    {
      sectionId: "products",
      href: "#products",
      label: t("products"),
      hasSubNav: true,
      subNavKey: "products",
    },
    {
      sectionId: "pricing",
      href: "/pricing",
      label: "Pricing",
      isRoute: true,
    },
    {
      sectionId: "about",
      href: "#about",
      label: t("about"),
      hasSubNav: true,
      subNavKey: "about",
    },
    { sectionId: "faq", href: "#faq", label: t("faq") },
  ];

  const linkStyles = {
    fontFamily: "body" as const,
    pos: "relative" as const,
    _after: {
      content: '""',
      position: "absolute" as const,
      bottom: "-4px",
      left: 0,
      width: "full",
      height: "2px",
      backgroundColor: "primary.500",
      transform: "scaleX(0)",
      transition: "transform 0.2s ease-in-out",
    },
    _hover: {
      _after: {
        transform: "scaleX(1)",
      },
    },
  };

  const desktopNav = (
    <HStack gap={12} textStyle="sm">
      {navigationItems.map((item) =>
        item.hasSubNav && item.subNavKey ? (
          <SubNavDropdown
            key={item.sectionId}
            navKey={item.subNavKey}
            label={item.label}
            color={headerColor}
            onScrollToSection={scrollToSection}
          />
        ) : (
          <NextLink
            key={item.sectionId}
            href={item.href}
            color={headerColor}
            {...linkStyles}
            onClick={(e) => {
              if (item.isRoute) {
                return;
              }
              handleLinkClick(e, item.sectionId);
            }}
            fontWeight="medium"
            fontFamily="beVietnamPro"
          >
            {item.label}
          </NextLink>
        ),
      )}
    </HStack>
  );

  const mobileNav = (onClose: () => void) =>
    navigationItems.map((item) =>
      item.hasSubNav && item.subNavKey ? (
        <SubNavMobileAccordion
          key={item.sectionId}
          navKey={item.subNavKey}
          label={item.label}
          onClose={onClose}
          onScrollToSection={scrollToSection}
        />
      ) : (
        <NextLink
          key={item.sectionId}
          href={item.href}
          color={headerColor}
          {...linkStyles}
          onClick={(e) => {
            if (!item.isRoute) {
              handleLinkClick(e, item.sectionId);
            }
            onClose();
          }}
          fontWeight="light"
          fontFamily="heading"
          textStyle="xl"
        >
          {item.label}
        </NextLink>
      ),
    );

  return (
    <PublicHeader
      navbar={desktopNav}
      mobileNavbar={mobileNav}
      maxW="1400px"
      showBookDemo={true}
    />
  );
};

interface BlurHeaderProps {
  translate: string;
  title: string;
  subtitle: string;
}

export const BlurHeader = ({ translate, title, subtitle }: BlurHeaderProps) => {
  const t = useTranslations(translate);
  return (
    <Center
      justifyItems="center"
      w="100%"
      position="sticky"
      top="0px"
      zIndex={50}
      p={{ base: 2, lg: 4 }}
      id="space-overview-sticky"
    >
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="themeColor.blur.alpha500"
        style={{
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
        zIndex={-1}
      />
      <Flex
        direction="column"
        gap={2}
        align="start"
        justify="start"
        w={{ base: "full", md: "70%" }}
        position="relative"
        zIndex={1}
        px={{ base: 2, md: 0 }}
      >
        <Text
          textStyle={{ base: "2xl", md: "3xl" }}
          color="themeColor.fg.title"
          maxW="600px"
          fontWeight="light"
          fontFamily="heading"
          textAlign="left"
        >
          {t(title)}
        </Text>
        <Text
          textStyle={{ base: "sm", md: "md" }}
          color="themeColor.fg.body"
          maxW="600px"
          textAlign="left"
        >
          {t(subtitle)}
        </Text>
      </Flex>
    </Center>
  );
};
